/**
 * @file weight.ts
 * @module app/modules/orders-delivery/utils/weight
 * @description Shared order weight display helper for the Orders Delivery module.
 * Returns a span node with a consistent label and title for total or processed weight.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 2.1.2
 */

import React from 'react';
import { OrderWeightLike } from '../types/order-metrics';

const normalizeWeight = (value: number | null | undefined) => {
  return Number(value ?? 0);
};

/**
 * Returns the total weight of an order, either from the explicit original total weight or by summing the weights of total quantities.
 * @param {OrderWeightLike} orderVal - The order weight object containing processed and original total weights.
 * @returns {number} The total weight of the order.
 */
const getTotalWeightByCalculatingQuantityWeights = (
  orderVal: OrderWeightLike
) => {
  return (
    orderVal?.totalQuantities?.reduce(
      (total, item) => total + normalizeWeight(item?.weight),
      0
    ) ?? 0
  );
};

/**
 * Normalizes a weight value, treating null or undefined as 0.
 * @param {number | null | undefined} value - The weight value to normalize.
 * @returns {number} The normalized weight value.
 */
const createOrderWeightSpan = (
  className: string | undefined,
  title: string | undefined,
  children: string | number
) => {
  return React.createElement(
    'span',
    {
      className: `${className ?? ''} order-weight`,
      title,
    },
    children
  );
};

/**
 * Converts kilograms to tons, normalizing the input value and formatting the result to two decimal places.
 * @param {number} kilograms - The weight in kilograms to convert.
 * @returns {string} The weight in tons, formatted to two decimal places.
 */
const defaultKgToTons = (kilograms: number) => {
  const conversionFactor = 0.001;
  const tons = normalizeWeight(kilograms) * conversionFactor;
  return tons.toFixed(3);
};

/**
 * Returns a span node using the original total-weight logic by summing totalQuantities.weight.
 * @param {OrderWeightLike} orderVal - The order weight object containing total quantities.
 * @param {string} [className] - Optional additional class name for the span element.
 * @param {(value: number) => string} [formatWeight=defaultKgToTons] - Weight formatter, typically kgToTons.
 * @returns {React.ReactElement} A React span element displaying the summed weight.
 */
export const getOrderWeightByCalculatingQuantityWeightsDisplay = (
  orderVal: OrderWeightLike,
  className?: string,
  formatWeight: (value: number) => string = defaultKgToTons
) => {
  const totalWeight = getTotalWeightByCalculatingQuantityWeights(orderVal);

  return createOrderWeightSpan(
    className,
    `total order ${formatWeight(totalWeight)} Ton`,
    `${formatWeight(totalWeight)} T`
  );
};